import { Banner, Button, Dialog, Input, Loader, Table, Text } from '@cloudflare/kumo'
import type {
  AdminPaymentGrantView,
  AdminPaymentLedgerView,
  AdminPaymentReview,
  AdminPaymentReviewTarget,
  Entitlements,
  PaymentGrantKind,
} from '@repo/shared'
import { useEffect, useState } from 'react'
import { request } from '../api'
import { useGetToken } from '../lib/auth'
import { fmtTime, toMessage } from '../lib/format'

const KIND: Record<PaymentGrantKind, string> = {
  quote: '견적권 구매',
  included: '플랜 포함 견적권',
  pack: '생성 팩',
  review: '전문가 검수권',
}

const reviewPath = (userId: string, paymentId: string) =>
  `/admin/users/${encodeURIComponent(userId)}/payments/${encodeURIComponent(paymentId)}/review`

function grantState(grant: AdminPaymentGrantView): string {
  if (grant.refundEventId) return `이미 환불 표시됨 (${grant.refundEventId})`
  if (grant.kind === 'pack') {
    const left = (grant.size ?? 0) - (grant.used ?? 0)
    return left > 0 ? `${grant.used}/${grant.size}회 사용 · 남은 ${left}회 회수 가능` : '모두 사용'
  }
  if (grant.quoteId) return `견적에 사용 (${grant.quoteId}) · 회수 안 함`
  if (grant.expertReviewId) return `전문가 검수에 사용 (${grant.expertReviewId}) · 회수 안 함`
  return '안 씀 · 회수 가능'
}

/**
 * 확인 필요 행 처리 창 (runbook-payment-exceptions.md). 대상 지급 행을 보여 주고, 사유를 적어야
 * '회수'(안 쓴 것만) 또는 '회수하지 않고 확인 완료'를 누를 수 있다. 서버가 같은 조건을 다시 검사한다.
 */
export function PaymentReviewDialog({
  userId,
  item,
  actionLabel,
  onDone,
  onClose,
}: {
  userId: string
  /** null 이면 닫힌 상태 */
  item: AdminPaymentLedgerView | null
  actionLabel: string
  onDone: (message: string, entitlements: Entitlements) => void
  onClose: () => void
}) {
  const getToken = useGetToken()
  const [target, setTarget] = useState<AdminPaymentReviewTarget | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const paymentId = item?.id ?? null

  useEffect(() => {
    if (!paymentId) return
    let current = true
    setTarget(null)
    setReason('')
    setError(null)
    request<AdminPaymentReviewTarget>(getToken, reviewPath(userId, paymentId))
      .then((result) => current && setTarget(result))
      .catch((cause: unknown) => current && setError(toMessage(cause)))
    return () => {
      current = false
    }
  }, [getToken, userId, paymentId])

  const submit = async (decision: AdminPaymentReview['decision']) => {
    if (!paymentId) return
    setBusy(true)
    setError(null)
    try {
      const body: AdminPaymentReview = { decision, reason: reason.trim() }
      const result = await request<{ revoked: number; entitlements: Entitlements }>(
        getToken,
        reviewPath(userId, paymentId),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      )
      onDone(
        decision === 'revoke'
          ? `안 쓴 지급분 ${result.revoked}건을 회수하고 확인 완료로 바꿨어요.`
          : '회수하지 않고 확인 완료로 바꿨어요.',
        result.entitlements,
      )
    } catch (cause) {
      setError(toMessage(cause))
    } finally {
      setBusy(false)
    }
  }

  const revocable = target?.grants.filter((g) => g.revocable).length ?? 0
  const noReason = !reason.trim()
  return (
    <Dialog.Root open={item !== null} onOpenChange={(open) => !open && onClose()}>
      <Dialog size="lg" className="p-6">
        <Dialog.Title className="text-lg font-semibold">환불 확인 처리</Dialog.Title>
        <Dialog.Description className="text-kumo-subtle mt-1">
          {actionLabel}. 회수 전에 RevenueCat·스토어에서 같은 거래가 전액 환불인지 확인해 주세요.
          회수는 아직 쓰지 않은 견적권(팩은 남은 횟수)만 빼고, 이미 견적에 쓴 것은 그대로 둬요.
        </Dialog.Description>
        <div className="mt-4 flex flex-col gap-4">
          {item && (
            <Text variant="secondary" size="xs">
              {fmtTime(item.createdAt)} · {item.productId ?? '상품 미확인'} · 이벤트 {item.eventId}
            </Text>
          )}
          {target === null && !error ? (
            <Loader />
          ) : target && target.grants.length === 0 ? (
            <Banner
              variant="alert"
              title="연결된 지급 행이 없어요"
              description="회수 버튼을 쓸 수 없어요. 런북의 '버튼을 쓸 수 없을 때' 절차를 따르거나, 확인 결과만 남겨 주세요."
            />
          ) : target ? (
            <div className="overflow-x-auto">
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.Head>지급 거래</Table.Head>
                    <Table.Head>종류</Table.Head>
                    <Table.Head>상태</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {target.grants.map((grant) => (
                    <Table.Row key={grant.transactionId}>
                      <Table.Cell>
                        <div className="max-w-56 break-all text-xs">{grant.transactionId}</div>
                        <div className="text-xs">{fmtTime(grant.purchasedAt)}</div>
                      </Table.Cell>
                      <Table.Cell>{KIND[grant.kind]}</Table.Cell>
                      <Table.Cell>{grantState(grant)}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          ) : null}
          {target && !target.canRevoke && target.grants.length > 0 && (
            <Text variant="secondary" size="xs">
              이 기록은 회수할 수 없어요(부분 환불·환불 취소·이미 쓴 견적권·자동 회수가 이미 처리한
              경우). 확인 결과만 남길 수 있어요.
            </Text>
          )}
          <Input
            label="처리 사유"
            placeholder="예: App Store 전액 환불 확인, 견적 미사용"
            maxLength={200}
            value={reason}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
          />
          {error && <Banner variant="error" title="처리하지 못했어요" description={error} />}
        </div>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button variant="secondary" disabled={busy} onClick={onClose}>
            취소
          </Button>
          <Button
            variant="secondary"
            loading={busy}
            disabled={noReason || target === null}
            onClick={() => void submit('dismiss')}
          >
            회수하지 않고 확인 완료
          </Button>
          <Button
            variant="destructive"
            loading={busy}
            disabled={noReason || !target?.canRevoke}
            onClick={() => void submit('revoke')}
          >
            {revocable > 0 ? `안 쓴 ${revocable}건 회수` : '회수'}
          </Button>
        </div>
      </Dialog>
    </Dialog.Root>
  )
}
